// src/components/Reports/widgets/KPIChartWidget.tsx

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MoreVertical } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import DashboardReportService from '@services/DashboardReportService';
import type {
  ReportWidget,
  ReportFilters,
  KPIChartItem,
  CustomFieldItem,
} from '@services/DashboardReportService';
import { ReportDatePicker } from '../ReportDatePicker';
import { ReportSelect } from '../ReportSelect';
import * as XLSX from 'xlsx';
import { Modal } from '@/components/utils/Modal';

interface KPIChartWidgetProps {
  widget: ReportWidget;
  dashboard: { DashboardId: number; ReportId: number; ProjectId?: number };
  filters: ReportFilters;
  onRemoveWidget: (widget: ReportWidget) => void;
  showSettings?: boolean;
  onUpdateSettings?: (WidgetId: number, newSettings: Record<string, unknown>) => void;
}

type ChartPer = 'Day' | 'Week' | 'Month';

function formatDateInput(d: Date): string
{
  return d.toISOString().split('T')[0]!;
}

const CHART_COLORS = ['#62accb', '#f97316', '#22c55e', '#a855f7', '#eab308'];

const chartPerOptions = [
  { value: 'Day', label: 'Day' },
  { value: 'Week', label: 'Week' },
  { value: 'Month', label: 'Month' },
];

function formatLabelDate(date: Date): string
{
  return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
}

function buildChartData(
  series: KPIChartItem[],
  startDate: string,
  endDate: string,
  chartPer: { value: string; label: string } | ChartPer,
): Record<string, string | number>[]
{
  const periodType = typeof chartPer === 'object' ? chartPer?.value : chartPer;
  const start = new Date(startDate);
  const end = new Date(endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  type Period = { label: string; start: Date; end: Date };
  const periods: Period[] = [];
  const cur = new Date(start);

  if (periodType === 'Day')
  {
    while (cur <= end)
    {
      const d = new Date(cur);
      periods.push({ label: formatLabelDate(d), start: d, end: new Date(d) });
      cur.setDate(cur.getDate() + 1);
    }
  }
  else if (periodType === 'Week')
  {
    // Align cursor to Sunday of the week containing startDate
    cur.setDate(cur.getDate() - cur.getDay());
    while (cur <= end)
    {
      const wStart = new Date(cur);
      const wEnd = new Date(cur);
      wEnd.setDate(wEnd.getDate() + 6);
      // Label shows the end (Saturday) of the week in mm/dd
      periods.push({ label: formatLabelDate(wEnd), start: wStart, end: wEnd });
      cur.setDate(cur.getDate() + 7);
    }
  }
  else
  {
    // Month — align to 1st of the month
    cur.setDate(1);
    while (cur <= end)
    {
      const mStart = new Date(cur);
      const mEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
      periods.push({ label: formatLabelDate(mStart), start: mStart, end: mEnd });
      cur.setMonth(cur.getMonth() + 1);
    }
  }

  const result: Record<string, string | number>[] = periods.map(p => ({ label: p.label }));

  for (const s of series)
  {
    periods.forEach((p, i) =>
    {
      let sum = 0;
      for (const pt of s.Data)
      {
        const ptDate = new Date(pt.CreatedAt);
        ptDate.setHours(0, 0, 0, 0);
        if (ptDate >= p.start && ptDate <= p.end)
        {
          sum += Number(pt.Value);
        }
      }
      (result[i] as Record<string, string | number>)[s.Name] = sum;
    });
  }

  return result;
}

function exportToExcel(
  series: KPIChartItem[],
  startDate: string,
  endDate: string,
  chartPer: { value: string; label: string } | ChartPer,
)
{
  const rows = buildChartData(series, startDate, endDate, chartPer);
  if (rows.length === 0) return;

  const seriesNames = series.filter(s => s.Name !== 'Total').map(s => s.Name);

  const wsData: (string | number)[][] = [
    ['Date', ...seriesNames],
    ...rows.map(row => [
      row.label as string,
      ...seriesNames.map(name => (row[name] as number) ?? 0),
    ]),
  ];

  const ws = XLSX.utils.aoa_to_sheet(wsData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'KPI Chart');

  const wbout = XLSX.write(wb, { bookType: 'xlsx', bookSST: true, type: 'binary' });

  function s2ab(s: string)
  {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
    return buf;
  }

  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const date = `${mm}-${dd}-${d.getFullYear()}`;
  const fileName = document.title.replace(/[^a-zA-Z ]/g, '');

  const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileName}_${date}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

function buildEmptyDateRange(
  startDate: string,
  endDate: string,
  chartPer: ChartPer,
): Record<string, string>[]
{
  const result: Record<string, string>[] = [];
  const end = new Date(endDate);
  const cur = new Date(startDate);
  while (cur <= end)
  {
    result.push({ label: formatLabelDate(cur) });
    if (chartPer === 'Month')
    {
      cur.setMonth(cur.getMonth() + 1);
    }
    else if (chartPer === 'Week')
    {
      cur.setDate(cur.getDate() + 7);
    }
    else
    {
      cur.setDate(cur.getDate() + 1);
    }
  }
  return result;
}

// Settings form row — label left, control right
function SettingsRow({
  label,
  blue,
  children,
}: {
  label: string;
  blue?: boolean;
  children: React.ReactNode;
})
{
  const isDarkRow = document.documentElement.classList.contains('dark');
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
      <div
        style={{
          width: 130,
          flexShrink: 0,
          fontSize: 12,
          color: blue ? '#62accb' : isDarkRow ? '#a9a093' : '#666',
        }}
      >
        {label}
      </div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

export const KPIChartWidget: React.FC<KPIChartWidgetProps> = ({
  widget,
  dashboard,
  filters,
  onRemoveWidget,
  onUpdateSettings,
  showSettings: showSettingsProp,
}) =>
{
  const settings = widget.DataValuesJson?.Settings ?? {};
  const isNew = settings.IsNew === 'true' || settings.IsNew === true;

  const today = new Date();
  const lastMonth = new Date(today);
  lastMonth.setMonth(today.getMonth() - 1);

  const [series, setSeries] = useState<KPIChartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(isNew || (showSettingsProp ?? false));

  const [startDate, setStartDate] = useState<string>(
    settings.StartDate
      ? new Date(settings.StartDate as any).toISOString()
      : new Date(new Date().setDate(new Date().getDate() - 90)).toISOString(),
  );
  const [endDate, setEndDate] = useState<string>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    settings.DueDate ? new Date(settings.DueDate as any).toISOString() : new Date().toISOString(),
  );
  const [chartPer, setChartPer] = useState(
    settings.TypePeriodSelected
      ? chartPerOptions[Number(settings.TypePeriodSelected)]
      : chartPerOptions[0],
  );

  // Settings state
  const [availableCardTypes, setAvailableCardTypes] = useState<{ id: number; name: string }[]>([]);
  const [availableFields, setAvailableFields] = useState<CustomFieldItem[]>([]);
  const [selectedCardType, setSelectedCardType] = useState<{ value: number; label: string }>(
    settings.CardType
      ? { value: Number(settings.CardType), label: String(settings.CardType) }
      : { value: 0, label: 'All Card Types' },
  );

  const [numericFields, setNumericFields] = useState<{ value: string; label: string }[]>(
    settings.Fields
      ? String(settings.Fields)
        .split(',')
        .map(x => ({ value: x, label: x }))
      : [],
  );
  const [addDynamicGroup, setAddDynamicGroup] = useState<boolean>(
    settings.AddDynamicGroup === 'true' || settings.AddDynamicGroup === true,
  );
  const [allPhasesOnly, setAllPhasesOnly] = useState<boolean>(
    settings.AllPhasesOnly === 'true' || settings.AllPhasesOnly === true,
  );
  const [deleteModal, setDeleteModal] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  const loadData = useCallback(async () =>
  {
    try
    {
      setLoading(true);
      setError(null);
      const result = await DashboardReportService.getKPIChartData({
        DashboardId: dashboard.DashboardId,
        WidgetId: widget.WidgetId,
        XMLFilters: filters,
        IsTrend: false,
        AllPhasesOnly: allPhasesOnly,
      });
      setSeries(result);
    }
    catch
    {
      setError('Failed to load KPI chart data.');
    }
    finally
    {
      setLoading(false);
    }
  }, [dashboard.DashboardId, widget.WidgetId, filters, allPhasesOnly]);

  useEffect(() =>
  {
    if (!showSettingsPanel)
    {
      loadData();
    }
  }, [loadData, showSettingsPanel]);

  // Load card types and numeric field options when settings open
  useEffect(() =>
  {
    if (!showSettingsPanel) return;
    DashboardReportService.getMenuByConfigValuePerson(dashboard.ProjectId ?? 0)
      .then(items =>
      {
        const mapped = (items as { ConfigValueID?: number; Name?: string }[]).map(item => ({
          id: item.ConfigValueID ?? 0,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          name: (item as any).Text ?? '',
        }));
        setAvailableCardTypes(mapped);
      })
      .catch(() => {});
    DashboardReportService.getCustomFieldsByTrend()
      .then(groups =>
      {
        // Legacy behavior: from each group take only the item with the lowest CustomFieldId
        const deduped = groups
          .filter(g => Array.isArray(g) && g.length > 0)
          .map(g => [...g].sort((a, b) => a.CustomFieldId - b.CustomFieldId)[0])
          .filter(Boolean) as CustomFieldItem[];
        setAvailableFields(deduped);
      })
      .catch(() => {});
  }, [showSettingsPanel, dashboard.DashboardId]);

  useEffect(() =>
  {
    const handleClick = (e: MouseEvent) =>
    {
      if (menuRef.current && !menuRef.current.contains(e.target as Node))
      {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const handleDelete = () =>
  {
    setMenuOpen(false);
    setDeleteModal(true);
  };

  const handleSaveSettings = async () =>
  {
    try
    {
      const fields = numericFields
        .filter(f => f.value && f.value !== 'Not Set')
        .map(x => x.value)
        .join(',');
      const cardType = availableCardTypes.find(ct => ct.id === selectedCardType.value);
      const request = {
        ReportId: widget.ReportId,
        WidgetId: widget.WidgetId,
        DashboardId: dashboard.DashboardId,
        IsNew: false,
        StartDate: startDate,
        DueDate: endDate,
        Fields: fields,
        AddDynamicGroup: addDynamicGroup,
        AllPhasesOnly: allPhasesOnly,
        IsTrend: false,
        TypePeriodSelected: chartPerOptions.findIndex(x => x.label === chartPer?.label),
        CardType: cardType?.name ?? 'All Card Types',
        ConfigValueID: selectedCardType.value,
      };
      await DashboardReportService.updateWidgetKPISettings(request);

      const newSettings = {
        AddDynamicGroup: request.AddDynamicGroup,
        CardType: request.CardType,
        ConfigValueID: request.ConfigValueID,
        DueDate: request.DueDate,
        Fields: request.Fields,
        IsNew: request.IsNew,
        StartDate: request.StartDate,
        TypePeriodSelected: request.TypePeriodSelected,
        AllPhasesOnly: request.AllPhasesOnly,
      };
      if (onUpdateSettings) onUpdateSettings(widget.WidgetId, newSettings);
      setShowSettingsPanel(false);
      loadData();
    }
    catch
    {
      // ignore
    }
  };

  useEffect(() =>
  {
    handleSaveSettings();
  }, [chartPer]);

  useEffect(() =>
  {
    loadData();
  }, [startDate, endDate, chartPer]);

  const deleteWidget = async () =>
  {
    try
    {
      await DashboardReportService.deleteWidget(widget.ReportId, widget.WidgetId);
      onRemoveWidget(widget);
    }
    catch {}
  };

  const isDark = document.documentElement.classList.contains('dark');
  const chartData = buildChartData(
    series,
    startDate,
    endDate,
    chartPer ?? { value: 'Day', label: 'Day' },
  );
  const displayData =
    chartData.length > 0
      ? chartData
      : buildEmptyDateRange(startDate, endDate, (chartPer as { value: ChartPer })?.value ?? 'Day');

  // Use field Name as value — API expects comma-separated names in Fields
  const fieldOptions = [
    { value: '', label: 'Select...' },
    ...availableFields.map(f => ({ value: f.Name, label: f.Name })),
  ];

  return (
    <>
      <div className="styled-widget-container">
        {/* Header */}
        <div className="widget-header">
          <span style={{ fontSize: 13, fontWeight: 500, color: isDark ? '#a9a093' : '#333' }}>
            {widget.Description || 'KPI Chart'}
          </span>
          <div className="relative" ref={menuRef}>
            <button
              className="p-1 hover:bg-gray-100 dark:hover:bg-[#3a3c3c] rounded dark:text-[#a9a093]"
              onClick={() => setMenuOpen(v => !v)}
              aria-label="Widget menu"
            >
              <MoreVertical size={16} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-7 z-50 bg-white dark:bg-[#333535] border border-gray-200 dark:border-[#4a4c4c] rounded shadow-md min-w-[160px]">
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-[#3a3c3c] dark:text-[#a9a093]"
                  onClick={() =>
                  {
                    setMenuOpen(false);
                    setShowSettingsPanel(true);
                  }}
                >
                  Settings
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-[#3a3c3c] text-red-600"
                  onClick={handleDelete}
                >
                  Delete Widget
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Controls — always visible */}
        <div
          className="burndown-header flex justify-center"
          style={{ borderBottom: `1px solid ${isDark ? '#4a4c4c' : '#f6f6f6'}` }}
        >
          <ReportDatePicker
            label="Start Date:"
            value={startDate}
            onChange={e => setStartDate(e ? formatDateInput(new Date(e)) : '')}
          />
          <ReportDatePicker
            label="End Date:"
            value={endDate}
            onChange={e => setEndDate(e ? formatDateInput(new Date(e)) : '')}
          />
          <ReportSelect
            label="Chart per:"
            value={chartPer}
            onChange={v => setChartPer(v as any)}
            options={chartPerOptions}
            width={100}
          />
          <button
            style={{
              fontSize: 11,
              color: isDark ? '#a9a093' : '#666',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              paddingLeft: '18px',
              paddingRight: '18px',
            }}
            onClick={() =>
              exportToExcel(series, startDate, endDate, chartPer ?? { value: 'Day', label: 'Day' })
            }
          >
            <i className="fa fa-cloud-download" style={{ fontSize: 14, color: '#aaa' }} />
            Export
          </button>
        </div>

        {/* Settings Panel */}
        {showSettingsPanel && (
          <div className="widget-content">
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: isDark ? '#a9a093' : '#333',
                marginBottom: 16,
              }}
            >
              Widget Settings
            </div>

            <SettingsRow label="Card Types:">
              <ReportSelect
                value={selectedCardType}
                onChange={v => setSelectedCardType(v as any)}
                options={[
                  { value: 0, label: 'All Card Types' },
                  ...availableCardTypes.map(ct => ({ value: ct.id, label: ct.name })),
                ]}
                width={200}
              />
            </SettingsRow>

            {[1, 2, 3, 4, 5].map((n, idx) => (
              <SettingsRow key={n} label={`Numeric Field ${n}:`}>
                <ReportSelect
                  value={numericFields[idx] ?? null}
                  onChange={v =>
                  {
                    const updated = [...numericFields];
                    updated[idx] = v as any;
                    setNumericFields(updated);
                  }}
                  options={fieldOptions}
                  width={200}
                />
              </SettingsRow>
            ))}

            <SettingsRow label="Include Dynamic Column">
              <span className="custom-checkbox task-card-checkbox">
                <input
                  type="checkbox"
                  id={`kpi-dynamic-${widget.WidgetId}`}
                  checked={addDynamicGroup}
                  onChange={e => setAddDynamicGroup(e.target.checked)}
                  className="taskCardCheckbox"
                />
                <label
                  htmlFor="CheckboxChangeAddDynamicGroup"
                  className="conversationCheckbox"
                  onClick={_e => setAddDynamicGroup(prev => !prev)}
                />
              </span>
            </SettingsRow>

            <SettingsRow label="All Phases Only">
              <span className="custom-checkbox task-card-checkbox">
                <input
                  type="checkbox"
                  id={`kpi-allphases-${widget.WidgetId}`}
                  checked={allPhasesOnly}
                  onChange={e => setAllPhasesOnly(e.target.checked)}
                  className="taskCardCheckbox"
                />
                <label
                  htmlFor="CheckboxAllPhasesOnly"
                  className="conversationCheckbox"
                  onClick={() => setAllPhasesOnly(prev => !prev)}
                />
              </span>
            </SettingsRow>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button
                style={{
                  fontSize: 12,
                  padding: '4px 14px',
                  border: `1px solid ${isDark ? '#4a4c4c' : '#ccc'}`,
                  borderRadius: 4,
                  background: isDark ? '#3a3c3c' : '#fff',
                  color: isDark ? '#a9a093' : '#555',
                  cursor: 'pointer',
                }}
                onClick={() => setShowSettingsPanel(false)}
              >
                Cancel
              </button>
              <button
                style={{
                  fontSize: 12,
                  padding: '4px 14px',
                  border: 'none',
                  borderRadius: 4,
                  background: '#62accb',
                  color: '#fff',
                  cursor: 'pointer',
                }}
                onClick={handleSaveSettings}
              >
                Save
              </button>
            </div>
          </div>
        )}

        {/* Chart */}
        {!showSettingsPanel && (
          <>
            <div className="flex flex-wrap gap-4 px-4 py-2">
              {series.length > 0 ? (
                series.map((s, i) => (
                  <div key={s.Name} className="text-center">
                    <div
                      className="text-base font-semibold"
                      style={{ color: CHART_COLORS[i % CHART_COLORS.length] }}
                    >
                      {s.Total}
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: CHART_COLORS[i % CHART_COLORS.length] }}
                    >
                      {s.Name}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center">
                  <div className="text-base font-semibold" style={{ color: '#62accb' }}>
                    0
                  </div>
                  <div className="text-xs" style={{ color: '#62accb' }}>
                    Total
                  </div>
                </div>
              )}
            </div>
            <div className="widget-content">
              {loading && (
                <p className="report-widget-feedback-message text-center">Loading KPI chart...</p>
              )}
              {error && (
                <p className="report-widget-feedback-message" style={{ color: '#c00' }}>
                  {error}
                </p>
              )}
              {!loading && !error && (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={displayData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid
                      stroke={isDark ? '#3a3c3c' : '#e8e8e8'}
                      vertical={true}
                      horizontal={false}
                    />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: isDark ? '#6e6860' : '#999' }}
                      axisLine={{ stroke: isDark ? '#4a4c4c' : '#e0e0e0' }}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: isDark ? '#6e6860' : '#999' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        fontSize: 11,
                        backgroundColor: isDark ? '#333535' : '#fff',
                        borderColor: isDark ? '#4a4c4c' : '#e5e5e5',
                        color: isDark ? '#a9a093' : '#333',
                      }}
                    />
                    {series.map((s, i) =>
                    {
                      const color = CHART_COLORS[i % CHART_COLORS.length];
                      return (
                        <Line
                          key={s.Name}
                          type="linear"
                          dataKey={s.Name}
                          stroke={color}
                          strokeWidth={1.5}
                          dot={{ r: 3, fill: color, strokeWidth: 0 }}
                          activeDot={{ r: 4 }}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </>
        )}
      </div>
      <Modal
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        title="Delete Widget"
        preventCloseClickOut
      >
        <p>Are you sure you want to delete it?</p>
        <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
          <button
            type="button"
            onClick={() =>
            {
              deleteWidget();
            }}
            className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1"
          >
            Ok
          </button>
          <button
            type="button"
            onClick={() => setDeleteModal(false)}
            className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1"
          >
            Cancel
          </button>
        </div>
      </Modal>
    </>
  );
};

export default KPIChartWidget;
